import type { ComponentType, ReactNode } from "react";
import { Check, Search, SlidersHorizontal, X } from "lucide-react";

import { cn } from "@/infrastructure/lib/utils";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";

export interface ListFilterMode<M extends string> {
  id: M;
  label: string;
}

export interface ListFilterOption<O extends string> {
  id: O;
  label: string;
  icon?: ComponentType<{ className?: string }>;
}

interface ListFilterToolbarProps<M extends string, O extends string> {
  query: string;
  onQueryChange: (query: string) => void;
  searchPlaceholder: string;
  clearSearchLabel: string;

  /** Grupo segmentado: escolha única. */
  modes: ListFilterMode<M>[];
  mode: M;
  onModeChange: (mode: M) => void;

  /** Popover de múltipla escolha. Sem opções, o botão não aparece. */
  optionsLabel: string;
  options: ListFilterOption<O>[];
  selected: O[];
  onToggleOption: (option: O) => void;

  /** Um controle a mais na mesma linha, antes de "limpar" (ex.: período). */
  extra?: ReactNode;

  /** Nenhum filtro ativo: esconde "limpar" e a mensagem de vazio. */
  clean: boolean;
  onClear: () => void;
  clearLabel: string;
  /** Quantos sobraram depois do filtro, para dizer quando nada sobrou. */
  total: number;
  noResultsLabel: string;
}

/**
 * Busca e filtros de uma lista do menu, no desenho da barra de Skills: uma
 * linha só, busca que cresce, escolha única num grupo segmentado e escolha
 * múltipla num popover. Jobs e Flows usam esta; Skills tem a própria, com as
 * etiquetas agrupadas por nome.
 */
export function ListFilterToolbar<M extends string, O extends string>({
  query,
  onQueryChange,
  searchPlaceholder,
  clearSearchLabel,
  modes,
  mode,
  onModeChange,
  optionsLabel,
  options,
  selected,
  onToggleOption,
  extra,
  clean,
  onClear,
  clearLabel,
  total,
  noResultsLabel,
}: ListFilterToolbarProps<M, O>) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          {/* `z-10`: o Input embrulha o campo num div com fundo opaco, e sem a
              camada o ícone fica atrás dele. */}
          <Search className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder={searchPlaceholder}
            className="h-9 pl-9 pr-9"
          />
          {query && (
            <button
              type="button"
              aria-label={clearSearchLabel}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
              onClick={() => onQueryChange("")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="flex h-9 shrink-0 items-center rounded-md border bg-muted/40 p-0.5">
          {modes.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onModeChange(item.id)}
              className={cn(
                "rounded-[5px] px-3 py-1 text-xs font-medium transition-colors",
                mode === item.id
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>

        {options.length > 0 && (
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 shrink-0 gap-1.5">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                {optionsLabel}
                {selected.length > 0 && (
                  <Badge variant="secondary" className="ml-0.5 h-5 px-1.5 text-[11px]">
                    {selected.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-2">
              {options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => onToggleOption(option.id)}
                  className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                >
                  <span className="flex items-center gap-2 truncate">
                    {option.icon && <option.icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                    {option.label}
                  </span>
                  {selected.includes(option.id) && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
                </button>
              ))}
            </PopoverContent>
          </Popover>
        )}

        {extra}

        {!clean && (
          <Button variant="ghost" size="sm" className="h-9 shrink-0 text-muted-foreground" onClick={onClear}>
            {clearLabel}
          </Button>
        )}
      </div>

      {total === 0 && !clean && <p className="py-6 text-center text-sm text-muted-foreground">{noResultsLabel}</p>}
    </div>
  );
}
